import { t } from "@lingui/macro";
import { useAtomValue, useSetAtom } from "jotai";

import { Crementor } from "@whoosh/design-system/src/components/controls/Crementor";
import { Avatar } from "@whoosh/design-system/src/components/display";
import { Text, HStack } from "@whoosh/design-system/src/components/primitive";

import { addPrivatePlayerAtom } from "../../../../atoms/actions/addPlayer/addPlayerAtom";
import { removeMemberHoldOrUnknownGuestAtom } from "../../../../atoms/actions/removeMemberHoldOrUnknownGuest/removeMemberHoldOrUnknownGuest";
import {
  currentBookingAtom,
  currentModalStepAtom,
} from "../../../../atoms/stores";

export const MemberHoldCounter = ({
  capacityOnBooking,
}: {
  capacityOnBooking: number;
}) => {
  const currentBooking = useAtomValue(currentBookingAtom);
  const currentModalStep = useAtomValue(currentModalStepAtom);

  const selectedBookingIndex =
    currentModalStep.type === "AddPlayerPrivate"
      ? currentModalStep.bookingIndex ?? 0
      : null;
  const rounds =
    currentBooking?.rounds.filter(
      (round) =>
        selectedBookingIndex == null ||
        (round.bookingIndex ?? 0) === selectedBookingIndex
    ) ?? [];

  const memberHoldPlayers = rounds.filter(
    (round) => round.player.clientType === "memberHold"
  );

  const totalHolds = memberHoldPlayers.length;
  const groupCapacity = currentBooking?.groupCapacity ?? capacityOnBooking;
  const memberHoldMaximum =
    groupCapacity -
    rounds.filter(
      (round) =>
        !round.isNonPlayingHost && round.player.clientType !== "memberHold"
    ).length;

  const addPlayer = useSetAtom(addPrivatePlayerAtom);
  const removeMemberHoldOrUnknownGuest = useSetAtom(
    removeMemberHoldOrUnknownGuestAtom
  );

  return currentBooking?.canAddMemberHold || totalHolds > 0 ? (
    <HStack
      px={6}
      justifyContent="space-between"
      alignItems="center"
      opacity={totalHolds >= memberHoldMaximum ? 0.5 : 1}
      py={2}
      my={2}
    >
      <HStack alignItems="center" gap={4}>
        <Avatar type="member" size="sm" useIcon />
        <Text
          maxFontSizeMultiplier={2}
          variant="mainBaseMedium"
        >{t`Member Hold`}</Text>
      </HStack>
      <Crementor
        testID="member-hold-crementor"
        min={0}
        max={memberHoldMaximum}
        value={totalHolds}
        onIncrement={() => {
          if (!currentBooking?.canAddMemberHold) return;

          addPlayer({
            player: {
              clientType: "memberHold",
              id: null,
              firstName: null,
              lastName: null,
            },
            bookingIndex:
              currentModalStep.type === "AddPlayerPrivate"
                ? currentModalStep.bookingIndex
                : undefined,
          });
        }}
        onDecrement={() => {
          // `removeMemberHoldOrUnknownGuestAtom` is a no-op when no Member
          // Hold round exists, and the Crementor's `min={0}` prevents extra
          // decrements past zero — no extra guard needed. The previous
          // `nextHoldToRemove` guard was dead code: `addPrivatePlayerAtom`
          // creates Member Holds with `player.id: null`, so the guard's
          // truthy check would always short-circuit and decrement would
          // silently no-op in production.
          removeMemberHoldOrUnknownGuest({
            clientType: "memberHold",
            bookingIndex: selectedBookingIndex ?? undefined,
          });
        }}
        isIncrementDisabled={!currentBooking?.canAddMemberHold}
      />
    </HStack>
  ) : null;
};
